import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAlgoliaAutocomplete, AutocompleteSuggestion } from '../hooks/api/useAlgoliaAutocomplete';
import { SearchIcon } from '../assets/images/svgs';

interface SearchInputProps {
  placeholder?: string;
  onSearch: (query: string) => void;
  debounceMs?: number;
  className?: string;
  initialValue?: string;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  placeholder = "What's on your mind today?",
  onSearch,
  debounceMs = 500,
  className = '',
  initialValue = '',
}) => {
  const [searchInput, setSearchInput] = useState(initialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const autocompleteQuery = useMemo(() => (showSuggestions ? searchInput : ''), [showSuggestions, searchInput]);
  const { suggestions: autocompleteSuggestions, isLoading: isLoadingAutocomplete } =
    useAlgoliaAutocomplete(autocompleteQuery);

  const clearDebounceTimer = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Sync internal state when initialValue prop changes
  useEffect(() => {
    setSearchInput(initialValue);
  }, [initialValue]);

  useEffect(() => {
    clearDebounceTimer();
    debounceTimerRef.current = setTimeout(() => {
      onSearch(searchInput);
    }, debounceMs);

    return clearDebounceTimer;
  }, [searchInput, onSearch, debounceMs, clearDebounceTimer]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestion: AutocompleteSuggestion) => {
      clearDebounceTimer();
      setSearchInput(suggestion.query);
      onSearch(suggestion.query);
      setShowSuggestions(false);
    },
    [clearDebounceTimer, onSearch],
  );

  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setShowSuggestions(true);
  }, []);

  const handleSearchInputFocus = useCallback(() => {
    if (searchInput.trim().length >= 2) {
      setShowSuggestions(true);
    }
  }, [searchInput]);

  const handleSearchInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        clearDebounceTimer();
        onSearch(searchInput);
        setShowSuggestions(false);
      } else if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    },
    [clearDebounceTimer, onSearch, searchInput],
  );

  return (
    <div className={`relative ${className}`}>
      <div className="border-1 bg-khLightGray inline-flex w-full min-w-[344px] max-w-[454px] flex-shrink-0 items-center justify-start gap-2 rounded-full border border-slate-300 px-4 py-1">
        <img src={SearchIcon} alt="Search" className="h-4 w-4" />
        <input
          ref={searchInputRef}
          type="text"
          placeholder={placeholder}
          value={searchInput}
          onChange={handleSearchInputChange}
          onFocus={handleSearchInputFocus}
          onKeyDown={handleSearchInputKeyDown}
          className="font-albert-sans !bg-khLightGray flex-1 border-none text-sm font-normal leading-5 text-black outline-none placeholder:text-gray-500 focus:ring-0 focus:ring-offset-0"
        />
      </div>
      {showSuggestions && autocompleteSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-10 mt-1 w-full max-w-[454px] rounded-lg border border-gray-300 bg-white shadow-lg"
        >
          {isLoadingAutocomplete && <div className="px-4 py-2 text-sm text-gray-500">Loading suggestions...</div>}
          {autocompleteSuggestions.map((suggestion, index) => (
            <button
              key={`${suggestion.query}-${index}`}
              onClick={() => handleSuggestionClick(suggestion)}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              dangerouslySetInnerHTML={{
                __html: suggestion.highlighted_query || suggestion.query,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
